import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { useEffect } from 'react';
import { AnimatePresence } from 'framer-motion';
import { HelmetProvider } from 'react-helmet-async';
import { SiteConfigProvider } from '@/context/SiteConfigContext';
import { AuthProvider } from '@/context/AuthContext';
import { ScrollToTop } from '@/components/ScrollToTop';
import { Navbar } from '@/components/Navbar';
import { Footer } from '@/components/Footer';
import { PageTransition } from '@/components/PageTransition';
import { ScrollProgress } from '@/components/ScrollProgress';
import { CustomCursor } from '@/components/CustomCursor';
import { IntroLoader } from '@/components/IntroLoader';
import { StickyBookNow } from '@/components/StickyBookNow';
import { MobileQuickBar } from '@/components/MobileQuickBar';
import { DigitalHUD } from '@/components/DigitalHUD';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import { Toaster } from '@/components/ui/sonner';
import { initGA4, trackPageView } from '@/lib/ga4';
import '@/App.css';

import Home from '@/pages/Home';
import Galleries from '@/pages/Galleries';
import GalleryDetail from '@/pages/GalleryDetail';
import Services from '@/pages/Services';
import ServiceDetail from '@/pages/ServiceDetail';
import Journal from '@/pages/Journal';
import JournalPost from '@/pages/JournalPost';
import About from '@/pages/About';
import Contact from '@/pages/Contact';
import BookRequest from '@/pages/BookRequest';
import ScoutReport from '@/pages/ScoutReport';
import CullReview from '@/pages/CullReview';
import ColorStudio from '@/pages/ColorStudio';
import StyleLibrary from '@/pages/StyleLibrary';
import Login from '@/pages/Login';
import ClientPortal from '@/pages/ClientPortal';
import ClientAccountSecurity from '@/pages/ClientAccountSecurity';
import AdminDashboard from '@/pages/AdminDashboard';
import SessionPlanner from '@/pages/SessionPlanner';
import Blueprint from '@/pages/Blueprint';
import ShootMode from '@/pages/ShootMode';
import SharedGallery from '@/pages/SharedGallery';
import SignupClient from '@/pages/SignupClient';
import SignupPhotographer from '@/pages/SignupPhotographer';
import Photographers from '@/pages/Photographers';
import PhotographerProfile from '@/pages/PhotographerProfile';
import HowItWorks from '@/pages/HowItWorks';
import GearLibrary from '@/pages/GearLibrary';
import GearLibraryDetail from '@/pages/GearLibraryDetail';
import ReviewSubmit from '@/pages/ReviewSubmit';
import NotFound from '@/pages/NotFound';

// Routes that render standalone (no marketing navbar/footer/floating CTAs).
const BARE_ROUTE_PREFIXES = ['/login', '/admin', '/client', '/shoot', '/share', '/signup'];

function Layout({ children }) {
  const { pathname } = useLocation();
  const isBare = BARE_ROUTE_PREFIXES.some((p) => pathname.startsWith(p));

  if (isBare) {
    return <div className="min-h-screen bg-[#0A0A0A] text-[hsl(var(--lxra-fg))]">{children}</div>;
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[hsl(var(--lxra-fg))] flex flex-col">
      <Navbar />
      <main className="flex-1 pb-16 lg:pb-0">{children}</main>
      <Footer />
    </div>
  );
}

function AnimatedRoutes() {
  const location = useLocation();
  return (
    <AnimatePresence mode="wait" initial={false}>
      <Routes location={location} key={location.pathname}>
        <Route path="/" element={<PageTransition><Home /></PageTransition>} />
        <Route path="/galleries" element={<PageTransition><Galleries /></PageTransition>} />
        <Route path="/galleries/:slug" element={<PageTransition><GalleryDetail /></PageTransition>} />
        <Route path="/services" element={<PageTransition><Services /></PageTransition>} />
        <Route path="/services/:slug" element={<PageTransition><ServiceDetail /></PageTransition>} />
        <Route path="/photographers" element={<PageTransition><Photographers /></PageTransition>} />
        <Route path="/p/:handle" element={<PageTransition><PhotographerProfile /></PageTransition>} />
        <Route path="/how-it-works" element={<PageTransition><HowItWorks /></PageTransition>} />
        <Route path="/gear" element={<PageTransition><GearLibrary /></PageTransition>} />
        <Route path="/gear/:slug" element={<PageTransition><GearLibraryDetail /></PageTransition>} />
        <Route path="/journal" element={<PageTransition><Journal /></PageTransition>} />
        <Route path="/journal/:slug" element={<PageTransition><JournalPost /></PageTransition>} />
        <Route path="/about" element={<PageTransition><About /></PageTransition>} />
        <Route path="/contact" element={<PageTransition><Contact /></PageTransition>} />
        <Route path="/book" element={<PageTransition><BookRequest /></PageTransition>} />
        <Route path="/review/:bookingId" element={<PageTransition><ReviewSubmit /></PageTransition>} />
        <Route path="/plan" element={<PageTransition><SessionPlanner /></PageTransition>} />
        <Route path="/plan/:id" element={<PageTransition><Blueprint /></PageTransition>} />
        <Route path="/login" element={<Login />} />
        <Route path="/client" element={<ProtectedRoute role="client"><ClientPortal /></ProtectedRoute>} />
        <Route path="/client/security" element={<ProtectedRoute role="client"><ClientAccountSecurity /></ProtectedRoute>} />
        <Route path="/admin" element={<ProtectedRoute role="admin"><AdminDashboard /></ProtectedRoute>} />
        <Route path="/admin/shoot/:id" element={<ProtectedRoute role="admin"><ShootMode mode="admin" /></ProtectedRoute>} />
        <Route path="/admin/scout/:id" element={<ProtectedRoute role="admin"><ScoutReport /></ProtectedRoute>} />
        <Route path="/admin/galleries/:slug/cull" element={<ProtectedRoute role="admin"><CullReview /></ProtectedRoute>} />
        <Route path="/admin/galleries/:slug/color" element={<ProtectedRoute role="admin"><ColorStudio /></ProtectedRoute>} />
        <Route path="/admin/color/styles" element={<ProtectedRoute role="admin"><StyleLibrary /></ProtectedRoute>} />
        <Route path="/shoot/:token" element={<ShootMode mode="shared" />} />
        <Route path="/share/:token" element={<SharedGallery />} />
        <Route path="/signup" element={<SignupClient />} />
        <Route path="/signup/photographer" element={<SignupPhotographer />} />
        <Route path="*" element={<PageTransition><NotFound /></PageTransition>} />
      </Routes>
    </AnimatePresence>
  );
}

// Initializes GA4 (only if REACT_APP_GA4_MEASUREMENT_ID is configured) and
// tracks SPA route changes as pageviews. No-op entirely until an ID is set.
function AnalyticsBootstrap() {
  const location = useLocation();

  useEffect(() => {
    initGA4();
  }, []);

  useEffect(() => {
    trackPageView(location.pathname + location.search);
  }, [location]);

  return null;
}

function FloatingCTAs() {
  const { pathname } = useLocation();
  const isBare = BARE_ROUTE_PREFIXES.some((p) => pathname.startsWith(p));
  if (isBare) return null;
  return (
    <>
      <StickyBookNow />
      <MobileQuickBar />
      <DigitalHUD />
    </>
  );
}

function App() {
  return (
    <HelmetProvider>
      <SiteConfigProvider>
        <BrowserRouter>
          <AuthProvider>
            <IntroLoader />
            <ScrollProgress />
            <CustomCursor />
            <ScrollToTop />
            <AnalyticsBootstrap />
            <Layout>
              <AnimatedRoutes />
            </Layout>
            <FloatingCTAs />
            <Toaster theme="dark" position="top-right" />
          </AuthProvider>
        </BrowserRouter>
      </SiteConfigProvider>
    </HelmetProvider>
  );
}

export default App;


